/**
 * Grooming error codes (epic PI-SPEC-SW-E279).
 *
 * Two families:
 * - **Engine error codes**: what the SpecTree Toolbox grooming engine
 *   reports when a run or an item fails (docs/architecture/grooming-app.md,
 *   section 2). The engine writes them to `PATCH /batches/:id/items/:id`
 *   as `errorCode` and sends them on its events; SpecTree pages show them.
 * - **API error codes**: the codes the SpecTree API returns that the engine
 *   branches on. Read them with {@link apiErrorCodeOf}: SpecTree puts a
 *   specific code in `error.details.code` when the error class carries a
 *   generic one (`FORBIDDEN`, `NOT_FOUND`, `CONFLICT`).
 */
import { z } from "zod";
// ---------------------------------------------------------------------------
// Engine error codes
// ---------------------------------------------------------------------------
/** Every code the grooming engine reports. */
export const GROOMING_ENGINE_ERROR_CODES = [
    "CLAUDE_NOT_INSTALLED",
    "CLAUDE_NOT_SIGNED_IN",
    "PLUGIN_MISSING",
    "MCP_NOT_CONNECTED",
    "USAGE_LIMIT",
    "NETWORK",
    "ITEM_NOT_READY",
    "SAVE_FAILED",
    "UNKNOWN",
    "REPO_AUTH_REQUIRED",
    "GIT_NOT_INSTALLED",
    // Contract 3 (epic PI-SPEC-SW-E284): the caller's Azure DevOps PAT is missing,
    // expired or refused. Contract-2 apps report MCP_NOT_CONNECTED for it.
    "ADO_PAT_INVALID",
];
/** Effect of each engine error code (grooming-app.md section 2). */
export const GROOMING_ENGINE_ERRORS = {
    CLAUDE_NOT_INSTALLED: { effect: "batch", stopReason: "environment" },
    CLAUDE_NOT_SIGNED_IN: { effect: "batch", stopReason: "environment" },
    PLUGIN_MISSING: { effect: "batch", stopReason: "environment" },
    MCP_NOT_CONNECTED: { effect: "batch", stopReason: "environment" },
    USAGE_LIMIT: { effect: "batch", stopReason: "usage-limit" },
    NETWORK: { effect: "batch", stopReason: "environment" },
    REPO_AUTH_REQUIRED: { effect: "batch", stopReason: "environment" },
    GIT_NOT_INSTALLED: { effect: "batch", stopReason: "environment" },
    ADO_PAT_INVALID: { effect: "batch", stopReason: "environment" },
    ITEM_NOT_READY: { effect: "item" },
    SAVE_FAILED: { effect: "item" },
    UNKNOWN: { effect: "item" },
};
/** True for a code this contract version knows. */
export function isGroomingEngineErrorCode(value) {
    return typeof value === "string" && GROOMING_ENGINE_ERROR_CODES.includes(value);
}
/**
 * Item `errorCode` the API itself sets when `POST /batches/:id/start` finds
 * an item below the grooming bar (the item is marked `not-ready`).
 */
export const GROOMING_BELOW_BAR_ERROR_CODE = "BELOW_GROOMING_BAR";
// ---------------------------------------------------------------------------
// API error codes
// ---------------------------------------------------------------------------
/** SpecTree API error codes the engine branches on. */
export const GROOMING_API_ERROR_CODES = [
    // Generic (error.code)
    "UNAUTHORIZED",
    "FORBIDDEN",
    "NOT_FOUND",
    "CONFLICT",
    "VALIDATION_ERROR",
    // The caller's own ADO PAT
    "ADO_PAT_REQUIRED",
    "ADO_PAT_INVALID",
    "ADO_RATE_LIMITED",
    "ADO_REQUEST_FAILED",
    // Who may do what
    "BOARD_AUDITOR_REQUIRED",
    "GROOMING_BATCH_FORBIDDEN",
    "GROOMING_BATCH_OWNER_REQUIRED",
    // Project setup
    "GROOMING_CONFIG_NOT_FOUND",
    "GROOMING_PROJECT_NOT_ENABLED",
    "TRACKER_TEAM_REQUIRED",
    // Batch state
    "GROOMING_BATCH_SINGLE",
    "GROOMING_BATCH_FULL",
    "GROOMING_BATCH_NOT_DRAFT",
    "GROOMING_BATCH_EMPTY",
    "GROOMING_BATCH_TRANSITION",
    "GROOMING_BATCH_ITEM_TRANSITION",
    "GROOMING_BATCH_ITEM_NO_BRIEF",
    // Contract 4 (epic PI-SPEC-SW-E323, ER-1710): agendas stay editable until
    // the meeting date passes. GROOMING_BATCH_NOT_DRAFT stays for batches that
    // are not meeting agendas.
    // The agenda's meeting date has passed (or it is closed), so it is read-only.
    "GROOMING_AGENDA_LOCKED",
    // The item was taken off the agenda while the run had it queued; the
    // engine skips it instead of reporting a failure.
    "GROOMING_BATCH_ITEM_REMOVED",
    // A move or add named a date that is not an upcoming meeting in the pattern.
    "GROOMING_SESSION_DATE_NOT_UPCOMING",
    // Another agenda already holds that meeting date.
    "GROOMING_SESSION_DATE_TAKEN",
    // Contract 5 (ER-1712): disregard a brief.
    // The brief is posted to ADO, so it cannot be disregarded.
    "GROOMING_BRIEF_POSTED",
    // The brief is already disregarded.
    "GROOMING_BRIEF_DISCARDED",
    // A newer brief was made for the item, so this one is no longer the one to disregard.
    "GROOMING_BRIEF_SUPERSEDED",
    // Undo is no longer possible: a newer brief exists or the item moved on.
    "GROOMING_DISREGARD_UNDO_UNAVAILABLE",
    // Brief codes SpecTree already returned before contract 5, listed here so
    // pages can branch on them: the brief is not held (not postable) / unknown.
    "GROOMING_BRIEF_NOT_HELD",
    "GROOMING_BRIEF_NOT_FOUND",
    // Items
    "WORK_ITEM_NOT_FOUND",
];
/** The body of every SpecTree API error response. */
export const apiErrorResponseSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
    }),
});
/**
 * The most specific code in a SpecTree error body: `error.details.code`
 * when it is a string, else `error.code`. Null when the body is not a
 * SpecTree error.
 */
export function apiErrorCodeOf(body) {
    const parsed = apiErrorResponseSchema.safeParse(body);
    if (!parsed.success)
        return null;
    const detailCode = parsed.data.error.details?.code;
    return typeof detailCode === "string" && detailCode.length > 0 ? detailCode : parsed.data.error.code;
}
//# sourceMappingURL=errors.js.map